import React, { useEffect, useState, useMemo } from 'react';
import { css, useTheme } from '@emotion/react';
import Grid from '../../grid/Grid';
import type { BannerProps } from 'tsconfig/types';
import { CmsImage } from '../CmsImage/CmsImage';
import { CmsLink } from '../CmsLink/CmsLink';
import { Button, inspectorMode, nextImageLoaderFile, useScreenLayout, VideoPlayer } from 'common-ui';
import { fontCorrectionInRichText } from '../../../utils/fontCorrectionInRichText';
import BannerWithContentAndCTA from '../../BannerComponents/BannerComponentWithContentAndCta';
import { BannerWithButton } from '../../BannerComponents/BannerComponentWithButton';
import {
  getClassNameBasedOnVariant,
  getContentWidth,
  getDataTestId,
  getTintedBackground,
  hasTintedBackground,
  getGradient,
} from '../../BannerComponents/BannerUtility';
import getPixalImageDataUrl from '../../../utils/getPixalImageDataUrl';
import {
  buildDLClickEventHandler,
  stopBannerContentEventPropagation,
  getButtonClass,
  getImageStyles,
  getBannerImageStyles,
  getVideoStyles,
  getBackgroundStyles,
  getContentType,
  getBannerTitle,
} from './CmsBannerUtility';

type CmsBannerType = React.MemoExoticComponent<({ contentItem }: BannerProps) => React.JSX.Element> & {
  BannerWithContentAndCTA: typeof BannerWithContentAndCTA;
};

const renderContentWidthBackground = (
  contentType: string,
  image: any,
  cmsImage: React.ReactElement,
  backgroundImageUrl: string,
  getVideoContent: () => React.ReactElement,
): React.ReactElement | null => {
  const isImageType = contentType === 'largeImage' || contentType === 'smallImage';
  const isVideoType = contentType === 'largeVideo' || contentType === 'smallVideo';

  if (isImageType) {
    if (image) {
      return null;
    }
    return <div className="banner-bg-img">{cmsImage}</div>;
  }

  return (
    <div
      className="banner-bg-img"
      style={{
        backgroundImage: `url(${backgroundImageUrl})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
      }}
    >
      {isVideoType ? getVideoContent() : ''}
    </div>
  );
};

type RenderImageColumnProps = {
  isCenterTintedBackground: boolean;
  contentWidth: string;
  bannerContentWidth: number;
  variant: string;
  tintedBackground: boolean;
  largeDeviceVimeoVideo?: string;
  renderExternalVideo: () => React.ReactElement;
  screenDetected: boolean;
  image: any;
  hideImageMobile: boolean;
  backgroundImage: any;
  contentWidthBackground: () => React.ReactElement | null;
  isAboveFold: boolean;
  dlClickEventHandler: ((data: string) => void) | undefined;
  bannerTitle?: string;
};

const renderImageColumn = ({
  isCenterTintedBackground,
  contentWidth,
  bannerContentWidth,
  variant,
  tintedBackground,
  largeDeviceVimeoVideo,
  renderExternalVideo,
  screenDetected,
  image,
  hideImageMobile,
  backgroundImage,
  contentWidthBackground,
  isAboveFold,
  dlClickEventHandler,
  bannerTitle,
}: RenderImageColumnProps): React.ReactElement | null => {
  if (isCenterTintedBackground) {
    return null;
  }

  return (
    <Grid.Col
      md={contentWidth ? 12 - bannerContentWidth : 6}
      xs={12}
      className={`banner-image-wrapper ${getClassNameBasedOnVariant(variant, false)}`}
    >
      {!tintedBackground && largeDeviceVimeoVideo
        ? renderExternalVideo()
        : screenDetected &&
          image &&
          !hideImageMobile && (
            <div className="banner-imgcomp">
              <CmsImage
                isNextImg={true}
                contentItem={image}
                fill={true}
                loading={isAboveFold ? 'eager' : 'lazy'}
                priority={isAboveFold}
                fetchPriority={isAboveFold ? 'high' : 'auto'}
                dlClickEventHandler={dlClickEventHandler}
                bannerTitle={bannerTitle}
              />
            </div>
          )}
      {!tintedBackground && backgroundImage && contentWidthBackground()}
    </Grid.Col>
  );
};

type RenderContentColumnProps = {
  bannerContentWidth: number;
  variant: string;
  alignment: string;
  bannerContent: string;
  theme: any;
  contentItem: any;
  CTALink: any[];
  bannerLink: any;
  dlClickEventHandler: any;
};

const renderContentColumn = ({
  bannerContentWidth,
  variant,
  alignment,
  bannerContent,
  theme,
  contentItem,
  CTALink,
  bannerLink,
  dlClickEventHandler,
}: RenderContentColumnProps): React.ReactElement => (
  <Grid.Col
    md={bannerContentWidth}
    xs={12}
    className={`banner-content-wrapper ${getClassNameBasedOnVariant(variant, true)}`}
  >
    <div data-testid={`banner-info-${alignment.toLowerCase()}`} className="banner-info">
      <section
        data-test="contentful-textbox"
        data-testid={`banner-content-${bannerContentWidth}`}
        className="banner-content"
      >
        {bannerContent && /<[a-z][\s\S]*>/i.test(bannerContent) && (
          <div
            onClick={stopBannerContentEventPropagation}
            onKeyDown={stopBannerContentEventPropagation}
            dangerouslySetInnerHTML={{ __html: fontCorrectionInRichText(bannerContent, theme.fonts) }}
            {...inspectorMode(contentItem?.sys?.id, 'bannerContent')}
            role="textbox"
            tabIndex={0}
          />
        )}
      </section>
      {CTALink.length > 0 && (
        <div className="banner-button">
          {CTALink.map((value: any) =>
            value?.fields?.minifiedButtonStyle ? (
              <a href={value?.fields?.url} key={value?.fields?.title}>
                <Button
                  className={getButtonClass(value?.fields?.variant)}
                  kind="tertiary"
                  {...inspectorMode(value?.sys?.id, 'title')}
                >
                  {value?.fields?.title}
                </Button>
              </a>
            ) : (
              <CmsLink
                key={`banner-cta-${value?.fields?.title}`}
                contentItem={value}
                dlClickEventHandler={bannerLink ? undefined : dlClickEventHandler}
              />
            ),
          )}
        </div>
      )}
    </div>
  </Grid.Col>
);

export const CmsBanner: CmsBannerType = Object.assign(
  React.memo(({ contentItem, dlClickEventHandler, isAboveFold = false }: BannerProps) => {
    const {
      variant = '',
      CTALink = [],
      contentBackgroundColour = '',
      bannerContent = '',
      alignment = '',
      image,
      backgroundImage,
      largeDeviceVimeoVideo,
      smallDeviceVimeoVideo,
      contentWidth = '',
      minimumBannerHeight,
      bannerLink,
      addGradient = true,
      hideImageOnMobile = false,
      entryTitle,
    } = contentItem.fields || {};

    const [videoAspectRatio, setVideoAspectRatio] = useState(9 / 16); // 16:9 default Vimeo Video Aspect ratio
    const theme = useTheme();
    const mediaSm = theme.breakpoints.sm ? theme.breakpoints.xs.replace('@media', '') : '';

    const { isNotDesktop } = useScreenLayout();
    const isMobileViewPort = typeof window !== 'undefined' && isNotDesktop;
    const [screenDetected, setScreenDetected] = useState(false);

    // Optimize image URL determination
    const backgroundImageUrl = useMemo(() => {
      if (!screenDetected) {
        return '';
      }
      const smallDeviceUrl = backgroundImage?.fields?.smallDeviceImage?.fields?.file?.url;
      const largeDeviceUrl = backgroundImage?.fields?.largeDeviceImage?.fields?.file?.url;
      const bgImage = isMobileViewPort ? smallDeviceUrl || largeDeviceUrl : largeDeviceUrl;
      return nextImageLoaderFile({ src: bgImage });
    }, [screenDetected, isMobileViewPort, backgroundImage]);

    const isCenterTintedBackground = variant === 'Full Background - Tinted Centre';
    const bannerContentWidth = getContentWidth(contentWidth);
    const tintedBackground = hasTintedBackground(variant);
    const hideImageMobile =
      hideImageOnMobile &&
      (variant === 'Content Left / Image Right' || variant === 'Content Right / Image Left') &&
      isMobileViewPort;

    useEffect(() => {
      setScreenDetected(true);
    }, []);

    // Use effect to preload images
    useEffect(() => {
      if (!isAboveFold) {
        return;
      }

      const preloadImage = (url: string | undefined) => {
        if (!url) {
          return;
        }
        const link = document.createElement('link');
        link.rel = 'preload';
        link.href = url;
        link.as = 'image';
        document.head.appendChild(link);
      };

      const smallImage = image?.fields?.smallDeviceImage?.fields?.file?.url;
      const largeImage = image?.fields?.largeDeviceImage?.fields?.file?.url;

      preloadImage(isMobileViewPort ? smallImage : largeImage);
      preloadImage(backgroundImageUrl);
    }, [image, backgroundImageUrl, isMobileViewPort, isAboveFold]);

    const styles = getImageStyles(tintedBackground, image, backgroundImageUrl || '');
    const bannerImageStyles = getBannerImageStyles(styles, minimumBannerHeight, theme);
    const videoStyles = getVideoStyles(largeDeviceVimeoVideo, variant, theme, videoAspectRatio);
    const backgroundStyles = getBackgroundStyles(tintedBackground, contentBackgroundColour, theme);

    // Find the Banner Title from the banner content for DL.
    const bannerTitle = getBannerTitle(bannerContent);

    const wrapper = css`
      ${theme.widgets.CmsBanner?.default};
      margin-bottom: 10px;
      container: banner / inline-size;
      height: 100%;
      ${hasTintedBackground(variant) && `position: relative;`}
      .banner {
        background-color: ${contentBackgroundColour};
        padding: 0;
        height: inherit;
        ${bannerLink && `cursor: pointer`};
        ${getTintedBackground(
          variant,
          largeDeviceVimeoVideo ? '' : backgroundImageUrl,
          theme.breakpoints.sm,
          addGradient,
        )}
        ${theme.breakpoints.md} {
          ${minimumBannerHeight && `min-height: ${minimumBannerHeight}px`};
        }
        .banner-image-wrapper {
          ${bannerImageStyles}
        }
        .banner-content-wrapper {
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto;
          ${backgroundStyles}
          text-align: ${alignment.toLowerCase()};
          .banner-info {
            .banner-button {
              justify-content: ${alignment.toLowerCase()};
            }
          }
        }
      }
      ${videoStyles}
      @container banner (width < ${Number.parseInt(theme.breakpoints.md.split(' ')[2]) / 2.13}px) {
        .banner {
          ${tintedBackground &&
          `background:
            ${addGradient ? `${getGradient(variant.replace('Full Background - Tinted ', ''), true)}, ` : ''}
            url(${backgroundImageUrl}) lightgray 50% / cover no-repeat;`}
          .banner-content-wrapper,
          .banner-image-wrapper {
            width: 100%;
          }
          .banner-info {
            padding: 16px;
          }
          .banner-content {
            margin-bottom: 12px;
            border-radius: 8px;
            ${theme.breakpoints.sm} {
              padding: 24px 16px;
              margin-bottom: 24px;
            }
            h2 {
              font-size: 30px;
              text-wrap: pretty;
            }
          }
        }
      }
    `;

    if (variant === 'Full width background banner with Calls to Action') {
      return <BannerWithButton contentItem={contentItem} dlClickEventHandler={dlClickEventHandler} />;
    }

    const externalVideoConfig = {
      vimeo: {
        playerOptions: {
          background: true,
          loop: true,
          playsinline: 1,
        },
        title: 'background-vimeo-player',
      },
    };

    const handlePlayerReady = (player: any) => {
      if (player?.getInternalPlayer) {
        const vimeoPlayer = player.getInternalPlayer();
        vimeoPlayer.getVideoWidth().then((width: number) => {
          vimeoPlayer.getVideoHeight().then((height: number) => {
            setVideoAspectRatio(height / width);
          });
        });
      }
    };

    const renderExternalVideo = () => (
      <VideoPlayer
        videoLink={largeDeviceVimeoVideo}
        smallDeviceVideoLink={smallDeviceVimeoVideo}
        config={externalVideoConfig}
        className="external-video-player"
        testId="external-video-player"
        loop={true}
        width="100%"
        height="100%"
        onReadyFn={handlePlayerReady}
      />
    );

    const cmsImage = (
      <CmsImage
        contentItem={backgroundImage}
        sizes={`${mediaSm}: ${((12 - bannerContentWidth) / 12) * 100}vw, 100vh`}
        fill={true}
        isNextImg={false}
        blurDataURL={getPixalImageDataUrl(contentBackgroundColour)}
        placeholder="blur"
        loading={isAboveFold ? 'eager' : 'lazy'}
        fetchPriority={isAboveFold ? 'high' : 'auto'}
        dlClickEventHandler={dlClickEventHandler}
        bannerTitle={bannerTitle}
      />
    );

    const getVideoContent = () => {
      const device = isMobileViewPort ? 'smallDeviceImage' : 'largeDeviceImage';
      return (
        <video
          autoPlay
          muted
          loop
          preload={isAboveFold ? 'auto' : 'none'}
          className="banner-bg-img desktop-video"
          style={{ width: '100%', height: '100%', position: 'absolute', top: '0', left: '0', objectFit: 'cover' }}
          src={
            backgroundImage?.fields[device]?.fields?.file?.url ??
            backgroundImage?.fields['largeDeviceImage']?.fields?.file?.url
          }
        >
          <source
            src={
              backgroundImage?.fields[device]?.fields?.file?.url ??
              backgroundImage?.fields['largeDeviceImage']?.fields?.file?.url
            }
            type={
              backgroundImage?.fields[device]?.fields?.file?.contentType ??
              backgroundImage?.fields['largeDeviceImage']?.fields?.file?.contentType
            }
          />
          Your browser does not support the video tag.
        </video>
      );
    };

    const contentType = getContentType(backgroundImage);

    const contentWidthBackground = () =>
      renderContentWidthBackground(contentType, image, cmsImage, backgroundImageUrl || '', getVideoContent);

    return (
      <div
        css={wrapper}
        data-test="banner"
        data-testid={`banner-${getDataTestId(variant)}`}
        onClick={e => buildDLClickEventHandler(e, dlClickEventHandler, entryTitle, bannerLink, bannerTitle)}
        onKeyDown={e => {
          if (e.key === 'Enter') {
            // Convert keyboard event to mouse-like event for data layer tracking
            buildDLClickEventHandler(e, dlClickEventHandler, entryTitle, bannerLink, bannerTitle);
          }
        }}
        role="button"
        tabIndex={0}
        className="banner-wrapper"
      >
        <Grid justify="start" className="banner">
          {renderImageColumn({
            isCenterTintedBackground,
            contentWidth,
            bannerContentWidth,
            variant,
            tintedBackground: hasTintedBackground(variant),
            largeDeviceVimeoVideo,
            renderExternalVideo,
            screenDetected,
            image,
            hideImageMobile,
            backgroundImage,
            contentWidthBackground,
            isAboveFold,
            dlClickEventHandler,
            bannerTitle,
          })}

          {renderContentColumn({
            bannerContentWidth,
            variant,
            alignment,
            bannerContent,
            theme,
            contentItem,
            CTALink,
            bannerLink,
            dlClickEventHandler,
          })}
        </Grid>
        {hasTintedBackground(variant) && largeDeviceVimeoVideo && renderExternalVideo()}
        {hasTintedBackground(variant) &&
          (contentType === 'largeVideo' || contentType === 'smallVideo') &&
          contentWidthBackground()}
      </div>
    );
  }),
  { BannerWithContentAndCTA },
);

CmsBanner.BannerWithContentAndCTA = BannerWithContentAndCTA;
